import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Grid } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import CardTitle from '../../common/card/CardTitle';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { Task } from '../tasks/Task.interface';
import TaskStatusCard from '../tasks/cards/TaskStatusCard';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import TaskStatusActions from '../tasks/TaskStatusActions';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import SupplierContactCard from '../supplier/cards/SupplierContactCard';
import {
  BaseSporCardProps,
  SupplierPurchaseOrder
} from '../supplier/Supplier.interface';
import { useFindPurchaseOrderByTask } from '../supplier/Supplier.hooks';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import OrderQuoteTable from './cards/OrderQuoteTable';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    display: flex;
    margin-top: 5px;
    .status-label {
      margin-right: 8px;
    }
  }
`;

const SporDetailPage: React.FC = () => {
  const { taskId: paramTaskId = '' } = useParams<{ taskId?: string }>();
  const taskId: number = parseInt(paramTaskId) || 0;
  const [spor, setSpor] = useState<SupplierPurchaseOrder | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { data, isLoading, error: findError, get } = useFindPurchaseOrderByTask(
    taskId
  );

  const cardProps: BaseSporCardProps = {
    spor: spor!,
    onChange: (newSpor: Partial<SupplierPurchaseOrder>) => {
      spor && setSpor({ ...spor, ...newSpor });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!spor?.task) {
      return;
    }
    Object.assign(spor.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    data && setSpor(data);
  }, [data]);

  useEffect(() => {
    setError(findError);
  }, [findError]);
  const { isFieldDisabled } = useTaskEntityFields(spor?.task);
  return (
    <PageTemplate>
      <PageTemplateHeader>
        <StyledTitle>
          <h2>{isLoading ? 'Loading...' : spor && `SPOR-${spor.id}`}</h2>
          {spor && (
            <div className='status-container'>
              <TaskStatusLabel task={spor.task} />
            </div>
          )}
        </StyledTitle>
        <PageTemplateHeaderActions flex>
          {spor?.task && (
            <TaskStatusActions
              task={spor.task}
              taskEntity={spor}
              onChange={(task: Task) => {
                spor.task = task;
                setSpor({ ...spor });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {spor && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                {spor.supplier && (
                  <Grid item xs={12}>
                    <SupplierContactCard supplier={spor.supplier} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Card>
                    <CardTitle>
                      <SubjectIcon />
                      Supplier Quote
                    </CardTitle>
                    <OrderQuoteTable {...cardProps} />
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              {spor.task && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TaskStatusCard task={spor.task} />
                  </Grid>
                  {spor.task.children && (
                    <Grid item xs={12}>
                      <SubTaskListCard
                        title='Stakeholders'
                        task={spor.task}
                        disabled={isFieldDisabled('assignSubTasks')}
                        onError={cardProps.onError}
                        onChange={handleTaskUpdate}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TaskFileListCard
                      task={spor.task}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        open={!!error}
        severity='error'
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Purchase Order details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default SporDetailPage;
